import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import TrialBanner from './TrialBanner';
import BrandMark from '../common/BrandMark';
import styles from './TopBar.module.css';
import { hasAdminModule } from '../../utils/staff';

interface TopBarProps {
  title?: string;
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
  actions?: React.ReactNode;
  onMenuClick?: () => void;
}

const getEnvLabel = (): string | null => {
  if (import.meta.env.DEV) return 'LOCAL';
  if (import.meta.env.VITE_APP_ENV === 'qa') return 'QA';
  return null;
};

const TopBar = ({ title, onSearch, searchPlaceholder = 'Search projects, files...', actions, onMenuClick }: TopBarProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const envLabel = getEnvLabel();

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    onSearch?.(e.target.value);
  };

  return (
    <header className={styles.topBar}>
      <div className="flex items-center gap-3 md:gap-8 flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {onMenuClick && (
            <button className={`${styles.iconButton} ${styles.menuButton}`} onClick={onMenuClick} aria-label="Open menu">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </svg>
            </button>
          )}
          {title ? (
            <h1 className="text-xl font-bold text-[#131b2e] font-headline tracking-tight">{title}</h1>
          ) : (
            <>
              <BrandMark size={30} decorative />
              <h1 className="text-2xl font-bold text-[#131b2e] font-headline tracking-tight">Infrio</h1>
            </>
          )}
          {envLabel && <span className={styles.envBadge} data-env={envLabel}>{envLabel}</span>}
        </div>
        <div className={styles.searchWrapper}>
          <span className={`material-symbols-outlined text-lg ${styles.searchIcon}`}>search</span>
          <input
            className={styles.searchInput}
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={handleSearch}
            type="text"
          />
        </div>
      </div>

      <TrialBanner />

      <div className="flex items-center gap-1.5 md:gap-3">
        {actions}
        {(user?.permissions?.releaseNotesVisible || hasAdminModule(user, 'release-notes')) && (
          <button className={styles.iconButton} onClick={() => navigate('/release-notes')} title="Release notes">
            <span className="material-symbols-outlined">campaign</span>
          </button>
        )}
        <button className={styles.iconButton}>
          <span className="material-symbols-outlined">notifications</span>
        </button>
        <button className={styles.iconButton} onClick={() => navigate('/profile')} title="Account settings" aria-label="Account settings">
          <span className="material-symbols-outlined">settings</span>
        </button>
        <div className={styles.divider} />
        <div
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/profile')}
        >
          <div className="text-right hidden sm:block">
            <p className="text-xs font-bold text-on-surface">{user?.firstName} {user?.lastName}</p>
            <p className="text-[10px] text-on-surface-variant capitalize">{user?.role}</p>
          </div>
          {user?.avatar ? (
            <img
              alt="User profile"
              className="w-9 h-9 rounded-full object-cover border border-primary/20"
              src={user.avatar}
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-primary-container flex items-center justify-center text-white text-xs font-bold border border-primary/20">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
